import React, { useEffect, useState } from "react";
import Header from "../header/header";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  Select,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./styles.css";
import PatientRecordsReport from "../patientRecordsReport/patientRecordsReport";
import FloatingButton from "../floatingButton/floatingButton";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import LoadingComponent from "../loadingComponent/loadingComponent";
import SelectFundusCamera from "../FundusCameraSelectionComponent/SelectFundusCamera";
import { saveFundusCameraList, getFundusCameraList, clearScreeningImageDraft } from "../../db";
import { getInferenceFromData } from "../../utlis";
import { initOpenCVWorker } from "../../services/opencvWorkerManager";

const DR_STAGE_OPTIONS = [
  { label: "No DR", api: "No Diabetic Retinopathy" },
  { label: "Mild NPDR", api: "Mild Diabetic Retinopathy" },
  { label: "Moderate NPDR", api: "Moderate Diabetic Retinopathy" },
  { label: "Severe NPDR", api: "Severe Diabetic Retinopathy" },
  { label: "Proliferative DR", api: "Proliferative Diabetic Retinopathy" },
];

const DME_STAGE_OPTIONS = [
  { label: "Absent", key: "Absent", api: "No DME" },
  { label: "Present", key: "Present", api: "DME" },
];

const QUICK_DIABETIC = "Diabetic Patients";
const QUICK_NON_DIABETIC = "Non Diabetic Patients";
const QUICK_FOLLOW_UP = "Screening Followup";
const QUICK_ASSESSMENT_PENDING = "Assessment pending"; // Ophthalmologist-only quick filter
const OPHTHALMOLOGIST_MIN_SCREENING_DATE = "2026-04-17";

const PatientRecords = () => {
  const navigate = useNavigate();
  const header = {
    Authorization: "Token " + localStorage.getItem("optha-token"),
  };
  const [allReports, setAllReports] = useState([]);
  const [next, setNext] = useState("");
  const [prev, setPrev] = useState("");
  const [count, setCount] = useState("");
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, count);
  const [getData, setGetData] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [fundusCameras, setFundusCameras] = useState([]);
  const [quickFilters, setQuickFilters] = useState([]);
  const [drGrades, setDrGrades] = useState([]);
  const [dmeStages, setDmeStages] = useState([]);
  const [drMenuAnchor, setDrMenuAnchor] = useState(null);
  const [dmeMenuAnchor, setDmeMenuAnchor] = useState(null);
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [screeningDateFrom, setScreeningDateFrom] = useState("");
  const [screeningDateTo, setScreeningDateTo] = useState("");
  const [openFundusCameraDialog, setOpenFundusCameraDialog] = useState(false);
  const [optometristRole, setOptometristRole] = useState(null);
  const [offline, setOffline] = useState(true); // Assume offline initially
  const [checkedConnectivity, setCheckedConnectivity] = useState(false);
  // District-wide facility scoping - Ophthalmologist only (CRD v4.2 US1/US2)
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null); // { id, name }
  const [facilityPickerOpen, setFacilityPickerOpen] = useState(false); // mobile bottom sheet
  const [facilityError, setFacilityError] = useState("");
  const isOphthalmologist = optometristRole === "Ophthalmologist";
  const searchLocked = isOphthalmologist && !selectedFacility;


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const openDrFilterMenu = (event) => {
    const el = event.currentTarget;
    setDmeMenuAnchor(null);
    setDateMenuAnchor(null);
    setDrMenuAnchor((prev) => (prev === el ? null : el));
  };

  const openDmeFilterMenu = (event) => {
    const el = event.currentTarget;
    setDrMenuAnchor(null);
    setDateMenuAnchor(null);
    setDmeMenuAnchor((prev) => (prev === el ? null : el));
  };

  const openDateFilterMenu = (event) => {
    const el = event.currentTarget;
    setDrMenuAnchor(null);
    setDmeMenuAnchor(null);
    setDateMenuAnchor((prev) => (prev === el ? null : el));
  };

  const formatShortDate = (isoDate) => {
    if (!isoDate) return "";
    const [y, m, d] = isoDate.split("-");
    return `${d}/${m}/${y.slice(2)}`;
  };

  const screeningDateFilterLabel =
    screeningDateFrom || screeningDateTo
      ? `${formatShortDate(screeningDateFrom) || "..."} - ${formatShortDate(screeningDateTo) || "..."}`
      : "Screening Date";

  // A labeled date field for use inside the Screening Date popover panel.
  // No onChange-triggered query here - the panel has its own "Done" button to apply both dates at once.
  const renderDateFilterInput = (label, value, onChange, minDate, maxDate) => (
    <Box>
      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#333", marginBottom: "6px" }}>{label}</Typography>
      <input
        type="date"
        value={value}
        onChange={onChange}
        min={minDate || undefined}
        max={maxDate || undefined}
        style={{
          border: "1px solid #ddd",
          borderRadius: "6px",
          padding: "8px 10px",
          fontSize: "14px",
          color: "#000",
          fontFamily: "inherit",
          background: "#fff",
          width: "100%",
          boxSizing: "border-box",
        }}
      />
    </Box>
  );

  useEffect(() => {
    const checkConnection = async () => {
      const { isOffline } = await import('../../utils/offlineDetection');
      const offlineStatus = await isOffline();
      setOffline(offlineStatus);
      setCheckedConnectivity(true);
    };
  
    // Check immediately
    checkConnection();
    
    // Check periodically (every 10 seconds)
    const interval = setInterval(checkConnection, 10000);
  
    const handleOnline = async () => {
      const { clearOfflineCache } = await import('../../utils/offlineDetection');
      clearOfflineCache();
      await checkConnection();
    };
    
    const handleOffline = () => {
      setOffline(true);
      // Verify with API check
      checkConnection();
    };
  
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  
    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);


  useEffect(() => {
    if (!checkedConnectivity) return;
    const role = localStorage.getItem("optometrist_role");
    setOptometristRole(role);
    if (role === "Ophthalmologist") {
      setScreeningDateFrom(OPHTHALMOLOGIST_MIN_SCREENING_DATE);
      setScreeningDateTo(new Date().toISOString().slice(0, 10));
    }
  }, [checkedConnectivity]);

  // Facility dropdown options come from the Ophthalmologist's own district (US1/US2)
  useEffect(() => {
    if (!isOphthalmologist || !checkedConnectivity || offline) return;
    const userProfile = JSON.parse(localStorage.getItem("user_profile"));
    const districtId = userProfile?.health_facility?.district;
    if (!districtId) return;
    axios
      .get(`${config.API_BASE_URL}/api/facilities/health-facility/?district=${districtId}`, { headers: header })
      .then((response) => {
        setFacilities(response.data?.results || response.data || []);
      })
      .catch((error) => console.log(error));
  }, [isOphthalmologist, checkedConnectivity, offline]);

  useEffect(() => {
    // Only fetch data if we're online and have checked connectivity
    // Ophthalmologists need internet to view records
    if (!checkedConnectivity || offline || optometristRole === null) {
      return;
    }
    fetchData();
  }, [optometristRole, offline, checkedConnectivity]);

  async function fetchData(facilityOverride = selectedFacility) {
    setGetData(true);

    const dateParams = [];
    if (screeningDateFrom) dateParams.push(`screening_date_from=${screeningDateFrom}`);
    if (screeningDateTo) dateParams.push(`screening_date_to=${screeningDateTo}`);
    if (isOphthalmologist && facilityOverride) dateParams.push(`facility_id=${facilityOverride.id}`);
    const url = `${config.API_BASE_URL}/api/reports/getreport/${dateParams.length ? `?${dateParams.join("&")}` : ""}`;

    await axios
      .get(url, { headers: header })
      .then((response) => {
        setAllReports(response.data.results);
        setNext(response.data.next);
        setPrev(response.data.previous);
        setCount(response.data.count);
        setCurrentPage(1);
        setQuickFilters([]);
        setDrGrades([]);
        setDmeStages([]);
      })
      .catch((error) => {
        console.log(error);
        if (error?.response?.status === 400 && error?.response?.data?.error) {
          setFacilityError(error.response.data.error);
        }
      });
    setGetData(false);
  }

  const handleSelectFacility = (facility) => {
    setSelectedFacility(facility);
    setFacilityPickerOpen(false);
    setSearchValue("");
    fetchData(facility);
  };

  const handleClearFacility = () => {
    setSelectedFacility(null);
    setSearchValue("");
    fetchData(null);
  };

  const handleInputChange = async (event) => {
    if (searchLocked) return;
    let newValue = event.target.value;
    setSearchValue(newValue);

    // Ophthalmologist: no auto-search per keystroke (that was wiping out active quick
    // filters on every query - CRD v4.2 US2 requires filters to stay active alongside
    // search). The explicit Search button below triggers the combined query instead;
    // clearing the box still reverts live so the filtered list comes right back.
    if (isOphthalmologist) {
      if (newValue === "") {
        runReportQuery(quickFilters, drGrades, dmeStages, screeningDateFrom, screeningDateTo, "");
      }
      return;
    }

    let newValuetrim = newValue.trim();

    if (newValuetrim.length >= 3 && newValuetrim !== searchValue.trim()) {
      try {
        // const ids = await axios
        // .get(`${config.API_BASE_URL}/api/auth/users/?role=patient&search_param=${newValue}`, {headers: header});
        // const patient_ids = ids.data.map(obj => obj.id);

        let searchUrl = `${config.API_BASE_URL}/api/reports/getreport/?search_param=${encodeURIComponent(newValuetrim)}`;
        if (isOphthalmologist && selectedFacility) searchUrl += `&facility_id=${selectedFacility.id}`;

        await axios
          .get(searchUrl, { headers: header })
          .then((response) => {
            setAllReports(response.data.results);
            setNext(response.data.next);
            setPrev(response.data.previous);
            setCount(response.data.count);
            setCurrentPage(1);
            setQuickFilters([]);
            setDrGrades([]);
            setDmeStages([]);
          })
          .catch((error) => {
            console.log(error);
            if (error?.response?.status === 400 && error?.response?.data?.error) {
              setFacilityError(error.response.data.error);
            }
          });
      } catch (error) {
        console.log(error);
      }
    } else if (newValue === "") {
      fetchData();
    }
  };

  // Use the imported handleSetDR from utlis.js which now supports model_outputs
  // This function is kept for backward compatibility but we'll use the imported one

  const handleNextButtonClick = async () => {
    setGetData(true);
    await axios
      .get(next, { headers: header })
      .then((response) => {
        setAllReports(response.data.results);
        setNext(response.data.next);
        setPrev(response.data.previous);
        setCount(response.data.count);
        setCurrentPage((prevPage) => prevPage + 1);
      })
      .catch((error) => console.log(error));
    setGetData(false);
  };

  const handlePrevButtonClick = async () => {
    setGetData(true);
    await axios
      .get(prev, { headers: header })
      .then((response) => {
        setAllReports(response.data.results);
        setNext(response.data.next);
        setPrev(response.data.previous);
        setCount(response.data.count);
        setCurrentPage((prevPage) => prevPage - 1);
      })
      .catch((error) => console.log(error));
    setGetData(false);
  };

  const handleFloatingClick = async () => {
    localStorage.removeItem("patient_id");
    localStorage.removeItem("is_follow_up_patient");
    localStorage.removeItem("follow_up_other_id");
    localStorage.removeItem("patient_form_draft");
    localStorage.removeItem("report_id");
    try {
      await clearScreeningImageDraft();
    } catch (_) { /* best-effort */ }

    initOpenCVWorker().catch((err) => {
      console.error('OpenCV worker init failed:', err);
    });

    if (!localStorage.getItem("fundusCamera")) {
      fetchCameras();
      setOpenFundusCameraDialog(true);
    } else {
      navigate("/patientscreen");
    }
  };

  const activeFilterCount =
    quickFilters.length + drGrades.length + dmeStages.length;
  const hasActiveFilters = activeFilterCount > 0;

  const clearAllFilters = () => {
    setQuickFilters([]);
    setDrGrades([]);
    setDmeStages([]);
    setDrMenuAnchor(null);
    setDmeMenuAnchor(null);
    const df = optometristRole === "Ophthalmologist" ? OPHTHALMOLOGIST_MIN_SCREENING_DATE : "";
    const dt = optometristRole === "Ophthalmologist" ? new Date().toISOString().slice(0, 10) : "";
    setScreeningDateFrom(df);
    setScreeningDateTo(dt);
    runReportQuery([], [], [], df, dt);
  };

  // Native date inputs fire onChange on every intermediate edit (e.g. paging through
  // months in the calendar), so just update local state here - the actual API call
  // only fires when the user clicks "Done" on the popover panel.
  // The input's own clear (X) button also fires onChange with "" - for Ophthalmologist,
  // that should fall back to the 17 Apr floor / today, not go blank (matches clearAllFilters).
  const handleScreeningDateFromChange = (event) => {
    const value = event.target.value;
    if (!value && optometristRole === "Ophthalmologist") {
      setScreeningDateFrom(OPHTHALMOLOGIST_MIN_SCREENING_DATE);
      return;
    }
    setScreeningDateFrom(value);
  };

  const handleScreeningDateToChange = (event) => {
    const value = event.target.value;
    if (!value && optometristRole === "Ophthalmologist") {
      setScreeningDateTo(new Date().toISOString().slice(0, 10));
      return;
    }
    setScreeningDateTo(value);
  };

  const handleApplyScreeningDateFilter = () => {
    setDateMenuAnchor(null);
    runReportQuery(quickFilters, drGrades, dmeStages, screeningDateFrom, screeningDateTo);
  };

  // Clears just the date range (unlike clearAllFilters, which also resets quick/DR/DME filters),
  // falling back to the same role-based default (17 Apr floor -> today for Ophthalmologist).
  const handleClearScreeningDateFilter = () => {
    const df = optometristRole === "Ophthalmologist" ? OPHTHALMOLOGIST_MIN_SCREENING_DATE : "";
    const dt = optometristRole === "Ophthalmologist" ? new Date().toISOString().slice(0, 10) : "";
    setScreeningDateFrom(df);
    setScreeningDateTo(dt);
    setDateMenuAnchor(null);
    runReportQuery(quickFilters, drGrades, dmeStages, df, dt);
  };

  const handleFundusCameraSave = (selectedCamera) => {
    localStorage.setItem("fundusCamera", selectedCamera);
    setOpenFundusCameraDialog(false);
    
};

  const runReportQuery = async (
    qf,
    dr,
    dme,
    dateFrom = screeningDateFrom,
    dateTo = screeningDateTo,
    searchOverride = searchValue,
    facilityOverride = selectedFacility
  ) => {
    setGetData(true);
    let apiUrl = "/api/reports/getreport/?";

    if (searchOverride.trim().length >= 3) {
      apiUrl += `search_param=${encodeURIComponent(searchOverride.trim())}&`;
    }

    if (dateFrom) apiUrl += `screening_date_from=${dateFrom}&`;
    if (dateTo) apiUrl += `screening_date_to=${dateTo}&`;
    if (isOphthalmologist && facilityOverride) apiUrl += `facility_id=${facilityOverride.id}&`;

    const hasFilters = qf.length > 0 || dr.length > 0 || dme.length > 0;
    if (hasFilters) {
      if (qf.includes(QUICK_FOLLOW_UP)) {
        apiUrl += "follow_up=true&";
      }

      if (qf.includes(QUICK_ASSESSMENT_PENDING)) {
        apiUrl += "assessment_pending=true&";
      }

      const hasDiabetic = qf.includes(QUICK_DIABETIC);
      const hasNonDiabetic = qf.includes(QUICK_NON_DIABETIC);
      if (!(hasDiabetic && hasNonDiabetic)) {
        if (hasDiabetic) apiUrl += "diabetes=Yes&";
        if (hasNonDiabetic) apiUrl += "diabetes=No&";
      }

      if (dr.length > 0) {
        apiUrl += `grade=${dr.join(",")}&`;
      }

      const dmeApi = [];
      if (dme.includes("Absent")) dmeApi.push("No DME");
      if (dme.includes("Present")) dmeApi.push("DME");
      if (dmeApi.length === 1) {
        apiUrl += `dme_grade=${encodeURIComponent(dmeApi[0])}&`;
      }
    }

    if (apiUrl.endsWith("&")) {
      apiUrl = apiUrl.slice(0, -1);
    }

    try {
      const response = await axios.get(`${config.API_BASE_URL}${apiUrl}`, {
        headers: header,
        timeout: 25000,
      });
      setAllReports(response.data.results || []);
      setNext(response.data.next);
      setPrev(response.data.previous);
      setCount(response.data.count || 0);
      setCurrentPage(1);
    } catch (error) {
      setAllReports([]);
      setCount(0);
      setNext("");
      setPrev("");
      if (error?.response?.status === 400 && error?.response?.data?.error) {
        setFacilityError(error.response.data.error);
      }
    } finally {
      setGetData(false);
    }
  };

  const toggleQuickFilter = (key) => {
    const next = quickFilters.includes(key)
      ? quickFilters.filter((k) => k !== key)
      : [...quickFilters, key];
    setQuickFilters(next);
    runReportQuery(next, drGrades, dmeStages);
  };

  const toggleDrGrade = (api) => {
    const next = drGrades.includes(api)
      ? drGrades.filter((g) => g !== api)
      : [...drGrades, api];
    setDrGrades(next);
    runReportQuery(quickFilters, next, dmeStages);
  };

  const toggleDmeStage = (key) => {
    const next = dmeStages.includes(key)
      ? dmeStages.filter((k) => k !== key)
      : [...dmeStages, key];
    setDmeStages(next);
    runReportQuery(quickFilters, drGrades, next);
  };

  const fetchCameras = async () => {
    try {
      const userProfile = JSON.parse(localStorage.getItem("user_profile"));
      const health_facility_id = userProfile?.health_facility?.id;
      if (!health_facility_id) return;
  
      // Try to fetch from API first
      try {
        const response = await axios.get(
          `${config.API_BASE_URL}/api/facilities/fundus-camera/?facility=${health_facility_id}`,
          { headers: header }
        );
  
        const cameraList = response.data.results;
        setFundusCameras(cameraList);
        
        // Save to IndexedDB for offline use
        await saveFundusCameraList(health_facility_id, cameraList);
  
        if (cameraList.length === 1) {
          localStorage.setItem("fundusCamera", cameraList[0].id);
          // Only navigate to patient screen if not ophthalmologist
          if (optometristRole !== "Ophthalmologist") {
            initOpenCVWorker().catch((err) => {
              console.error('OpenCV worker init failed:', err);
            });
            navigate("/patientscreen");
          }
        } else {
          // Only show camera dialog if not ophthalmologist
          if (optometristRole !== "Ophthalmologist") {
            setOpenFundusCameraDialog(true);
          }
        }
      } catch (apiError) {
        console.error("Error fetching cameras from API:", apiError);
        
        // If offline or network error, try to load from IndexedDB
        if (apiError.code === 'ERR_NETWORK' || 
            apiError.message === 'Network Error' ||
            apiError.message.includes('ERR_INTERNET_DISCONNECTED')) {
          const savedCameraList = await getFundusCameraList(health_facility_id);
          if (savedCameraList && savedCameraList.length > 0) {
            setFundusCameras(savedCameraList);
            
            if (savedCameraList.length === 1) {
              localStorage.setItem("fundusCamera", savedCameraList[0].id);
              // Only navigate to patient screen if not ophthalmologist
              if (optometristRole !== "Ophthalmologist") {
                initOpenCVWorker().catch((err) => {
                  console.error('OpenCV worker init failed:', err);
                });
                navigate("/patientscreen");
              }
            } else {
              // Only show camera dialog if not ophthalmologist
              if (optometristRole !== "Ophthalmologist") {
                setOpenFundusCameraDialog(true);
              }
            }
          } else {
            console.error('No saved camera list found in IndexedDB');
          }
        }
      }
    } catch (error) {
      console.error("Error in fetchCameras:", error);
    }
  };

  // While checking network, show main loader (no separate "Checking network status..." screen)
  if (!checkedConnectivity) {
    return (
      <>
        {!isMobile && <Header />}
        <LoadingComponent open={true} text={false} />
      </>
    );
  }

  // Show offline screen when offline
  // For ophthalmologists: show message (no add button, no fundus camera dialog)
  // For optometrists: show message with add button
  if (offline) {
    return (
      <>
        {!isMobile && <Header />}
        <Box
          sx={{
            display: "flex",
            height: "80vh",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: "20px",
            textAlign: "center",
            padding: "20px",
          }}
        >
          {/* Cloud Icon */}
          <Box
            sx={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "10px",
            }}
          >
            <Box
              sx={{
                width: "50px",
                height: "30px",
                border: "2px solid #ccc",
                borderRadius: "25px",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "-15px",
                  left: "10px",
                  width: "20px",
                  height: "20px",
                  border: "2px solid #ccc",
                  borderRadius: "50%",
                  backgroundColor: "#f5f5f5",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "-15px",
                  left: "25px",
                  width: "15px",
                  height: "15px",
                  border: "2px solid #ccc",
                  borderRadius: "50%",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
            {/* Diagonal line through cloud */}
            <Box
              sx={{
                position: "absolute",
                width: "60px",
                height: "2px",
                backgroundColor: "#ccc",
                transform: "rotate(45deg)",
                transformOrigin: "center",
              }}
            />
          </Box>

          {/* Headline */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: "#000000",
              marginBottom: "10px",
            }}
          >
            No Internet, No Problem.
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: "#666666",
              maxWidth: "400px",
              marginBottom: "30px",
            }}
          >
            {optometristRole === "Ophthalmologist" 
              ? "It looks like you are not connected to the internet right now. Please connect to the internet to view patient records."
              : "It looks like you are not connected to the internet right now, but you can still continue screening patients."}
          </Typography>

          {/* Add Patient Records Button - Only show for Optometrists, not Ophthalmologists */}
          {optometristRole !== "Ophthalmologist" && (
            <Button
              variant="contained"
              startIcon={<Box component="span" sx={{ fontSize: "20px", fontWeight: "bold" }}>+</Box>}
              onClick={handleFloatingClick}
              sx={{
                backgroundColor: "#0D1E83",
                color: "white",
                borderRadius: "8px",
                padding: "12px 24px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "500",
                "&:hover": {
                  backgroundColor: "#051572",
                },
              }}
            >
              Add Patient Records
            </Button>
          )}

        </Box>
      </>
    );
  }


  return (
    <>
      {!isMobile && <Header />}
      {/* Only show fundus camera dialog for Optometrists, not Ophthalmologists */}
      {optometristRole !== "Ophthalmologist" && (
        <SelectFundusCamera
              open={openFundusCameraDialog}
              handleClose={() => setOpenFundusCameraDialog(false)}
              handleSave={handleFundusCameraSave}
              setOpenFundusCameraDialog={setOpenFundusCameraDialog}
              cameras={fundusCameras}
          />
      )}
      {isOphthalmologist && (
        <Drawer
          anchor="bottom"
          open={facilityPickerOpen}
          onClose={() => setFacilityPickerOpen(false)}
          PaperProps={{ sx: { borderTopLeftRadius: "16px", borderTopRightRadius: "16px", maxHeight: "60vh" } }}
        >
          <Box sx={{ padding: "16px" }}>
            <Typography sx={{ fontWeight: 600, textAlign: "center", marginBottom: "8px" }}>
              Select Facility
            </Typography>
            <List>
              {facilities.map((f) => (
                <ListItemButton
                  key={f.id}
                  onClick={() => handleSelectFacility(f)}
                  selected={selectedFacility?.id === f.id}
                >
                  <ListItemText primary={f.name} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Drawer>
      )}
      <Snackbar
        open={!!facilityError}
        autoHideDuration={5000}
        onClose={() => setFacilityError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setFacilityError("")}>
          {facilityError}
        </Alert>
      </Snackbar>
      <div className="recordsDiv">
        <div className="patient-records-toolbar">
        <div className="searchDiv" style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <div className="patient-records-dual-search-row" style={{ display: "flex", gap: "12px", justifyContent: "center", alignItems: "flex-start" }}>
            {isOphthalmologist && (
              <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: isMobile ? 1 : "0 0 auto", minWidth: 0 }}>
                {isMobile ? (
                  <Paper
                    className="searchBar patient-records-search-paper"
                    onClick={() => setFacilityPickerOpen(true)}
                    sx={{ cursor: "pointer" }}
                  >
                    <IconButton disabled>
                      <ApartmentIcon />
                    </IconButton>
                    <Typography noWrap sx={{ flex: 1, color: selectedFacility ? "#000" : "#696969" }}>
                      {selectedFacility ? selectedFacility.name : "Select Facility"}
                    </Typography>
                    <ChevronRightIcon sx={{ color: "#999", flexShrink: 0 }} />
                  </Paper>
                ) : (
                  <Paper className="searchBar patient-records-search-paper">
                    <IconButton disabled>
                      <ApartmentIcon />
                    </IconButton>
                    <Select
                      displayEmpty
                      variant="standard"
                      disableUnderline
                      value={selectedFacility?.id || ""}
                      onChange={(event) => {
                        const facility = facilities.find((f) => f.id === event.target.value);
                        if (facility) handleSelectFacility(facility);
                      }}
                      renderValue={() =>
                        selectedFacility ? (
                          selectedFacility.name
                        ) : (
                          <span style={{ color: "#696969" }}>Select Facility</span>
                        )
                      }
                      MenuProps={{
                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                        transformOrigin: { vertical: "top", horizontal: "left" },
                        PaperProps: {
                          sx: {
                            mt: "6px",
                            borderRadius: "10px",
                            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
                            minWidth: "220px",
                          },
                        },
                        MenuListProps: { sx: { padding: "4px" } },
                      }}
                      sx={{
                        flex: 1,
                        fontSize: "14px",
                        "& .MuiSelect-select:focus": { backgroundColor: "transparent" },
                      }}
                    >
                      {facilities.map((f) => (
                        <MenuItem key={f.id} value={f.id} sx={{ padding: "10px 14px", borderRadius: "6px" }}>
                          {f.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {selectedFacility && (
                      <IconButton size="small" onClick={handleClearFacility} aria-label="clear facility">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Paper>
                )}
                {searchLocked && (
                  <Typography sx={{ color: "#CC0505", fontSize: "13px", display: "flex", alignItems: "center", gap: "4px" }}>
                    <ErrorOutlineIcon sx={{ fontSize: "16px" }} />
                    Please select a facility first
                  </Typography>
                )}
              </div>
            )}
            <Paper className={`searchBar patient-records-search-paper${searchLocked ? " patient-records-search-paper--disabled" : ""}`}>
              <IconButton disabled={searchLocked}>
                <SearchIcon />
              </IconButton>
              <InputBase
                className="patient-records-search-input"
                placeholder="Search by Patient ID"
                inputProps={{ "aria-label": "search patient reports" }}
                onChange={handleInputChange}
                value={searchValue}
                disabled={searchLocked}
              />
            </Paper>
            {isOphthalmologist && !isMobile && (
              <Button
                variant="contained"
                disableElevation
                startIcon={<SearchIcon />}
                disabled={!selectedFacility || !searchValue.trim()}
                onClick={() => runReportQuery(quickFilters, drGrades, dmeStages)}
                sx={{
                  backgroundColor: "#0D1E83",
                  textTransform: "none",
                  borderRadius: "9px",
                  paddingX: "20px",
                  flexShrink: 0,
                  "&:hover": { backgroundColor: "#051572" },
                  "&.Mui-disabled": { backgroundColor: "#e0e0e0", color: "rgba(0, 0, 0, 0.38)" },
                }}
              >
                Search
              </Button>
            )}
          </div>
          {isOphthalmologist && isMobile && (
            <Button
              variant="contained"
              disableElevation
              fullWidth
              startIcon={<SearchIcon />}
              disabled={!selectedFacility || !searchValue.trim()}
              onClick={() => runReportQuery(quickFilters, drGrades, dmeStages)}
              sx={{
                backgroundColor: "#0D1E83",
                textTransform: "none",
                borderRadius: "9px",
                "&:hover": { backgroundColor: "#051572" },
                "&.Mui-disabled": { backgroundColor: "#e0e0e0", color: "rgba(0, 0, 0, 0.38)" },
              }}
            >
              Search
            </Button>
          )}
        </div>
        <div className="filterDiv">
          <Box className="patient-records-filter-outer-row">
            <Box className="patient-records-filter-controls">
            {isMobile ? (
              <Box className="patient-records-filter-mobile-column">
                <Box className="patient-records-filter-mobile-top-row">
                  <Box className="patient-records-filter-mobile-leading">
                    <Box className="patient-records-filter-icon-wrap">
                      <FilterAltOutlinedIcon />
                    </Box>
                    <Typography className="patient-records-filter-label--mobile">
                      Filter:
                    </Typography>
                    <Box className="patient-records-filter-count-badge--mobile">
                      {activeFilterCount}
                    </Box>
                  </Box>
                  <Box className="patient-records-filter-scroll-mobile">
                    <Chip
                      label={QUICK_DIABETIC}
                      variant="outlined"
                      size="small"
                      onClick={() => toggleQuickFilter(QUICK_DIABETIC)}
                      className={`patient-records-filter-chip-mobile${quickFilters.includes(QUICK_DIABETIC) ? " patient-records-filter-chip-mobile--selected" : ""}`}
                    />
                    <Chip
                      label={QUICK_NON_DIABETIC}
                      variant="outlined"
                      size="small"
                      onClick={() => toggleQuickFilter(QUICK_NON_DIABETIC)}
                      className={`patient-records-filter-chip-mobile${quickFilters.includes(QUICK_NON_DIABETIC) ? " patient-records-filter-chip-mobile--selected" : ""}`}
                    />
                    <Chip
                      label={QUICK_FOLLOW_UP}
                      variant="outlined"
                      size="small"
                      onClick={() => toggleQuickFilter(QUICK_FOLLOW_UP)}
                      className={`patient-records-filter-chip-mobile${quickFilters.includes(QUICK_FOLLOW_UP) ? " patient-records-filter-chip-mobile--selected" : ""}`}
                    />
                    {optometristRole === "Ophthalmologist" && (
                      <Chip
                        label={QUICK_ASSESSMENT_PENDING}
                        variant="outlined"
                        size="small"
                        onClick={() => toggleQuickFilter(QUICK_ASSESSMENT_PENDING)}
                        className={`patient-records-filter-chip-mobile${quickFilters.includes(QUICK_ASSESSMENT_PENDING) ? " patient-records-filter-chip-mobile--selected" : ""}`}
                      />
                    )}
                    <Chip
                      variant="outlined"
                      size="small"
                      onClick={openDrFilterMenu}
                      className={[
                        "patient-records-filter-stage-chip-mobile",
                        drGrades.length > 0 ? "patient-records-filter-stage-chip-mobile--filled" : "",
                        drGrades.length === 0 && Boolean(drMenuAnchor)
                          ? "patient-records-filter-stage-chip-mobile--menu-open"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      label={
                        <Box className="patient-records-filter-stage-chip-label-row">
                          <Typography
                            component="span"
                            className="patient-records-filter-stage-chip-label-text"
                          >
                            {drGrades.length > 0
                              ? `${drGrades.length} DR Stage`
                              : "DR Stage"}
                          </Typography>
                          {drGrades.length > 0 && (
                            <Box
                              component="span"
                              className="patient-records-filter-stage-chip-count-dot"
                            >
                              {drGrades.length}
                            </Box>
                          )}
                          <KeyboardArrowDownIcon className="patient-records-filter-chevron" />
                        </Box>
                      }
                    />
                    <Chip
                      variant="outlined"
                      size="small"
                      onClick={openDmeFilterMenu}
                      className={[
                        "patient-records-filter-stage-chip-mobile",
                        dmeStages.length > 0 ? "patient-records-filter-stage-chip-mobile--filled" : "",
                        dmeStages.length === 0 && Boolean(dmeMenuAnchor)
                          ? "patient-records-filter-stage-chip-mobile--menu-open"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      label={
                        <Box className="patient-records-filter-stage-chip-label-row patient-records-filter-stage-chip-label-row--dme">
                          <Typography
                            component="span"
                            className="patient-records-filter-stage-chip-label-text"
                          >
                            {dmeStages.length > 0
                              ? `${dmeStages.length} DME`
                              : "DME"}
                          </Typography>
                          <KeyboardArrowDownIcon className="patient-records-filter-chevron" />
                        </Box>
                      }
                    />
                    <Chip
                      variant="outlined"
                      size="small"
                      onClick={openDateFilterMenu}
                      className={[
                        "patient-records-filter-stage-chip-mobile",
                        (screeningDateFrom || screeningDateTo) ? "patient-records-filter-stage-chip-mobile--filled" : "",
                        !(screeningDateFrom || screeningDateTo) && Boolean(dateMenuAnchor)
                          ? "patient-records-filter-stage-chip-mobile--menu-open"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      label={
                        <Box className="patient-records-filter-stage-chip-label-row">
                          <Typography component="span" className="patient-records-filter-stage-chip-label-text">
                            {screeningDateFilterLabel}
                          </Typography>
                          <KeyboardArrowDownIcon className="patient-records-filter-chevron" />
                        </Box>
                      }
                    />
                    {hasActiveFilters && (
                      <Typography
                        component="button"
                        type="button"
                        onClick={clearAllFilters}
                        className="patient-records-clear-filters--inline"
                      >
                        Clear
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%", flexWrap: "nowrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                  <Box className="patient-records-filter-icon-wrap">
                    <FilterAltOutlinedIcon />
                  </Box>
                  <Typography className="patient-records-filter-label--desktop">
                    Filter by:
                  </Typography>
                  {hasActiveFilters && (
                    <Box className="patient-records-filter-count-badge--desktop">
                      {activeFilterCount}
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px", flex: "1 1 auto", minWidth: 0 }}>
                  <Chip
                    label={QUICK_DIABETIC}
                    variant="outlined"
                    size="medium"
                    onClick={() => toggleQuickFilter(QUICK_DIABETIC)}
                    className={`patient-records-filter-chip-desktop${quickFilters.includes(QUICK_DIABETIC) ? " patient-records-filter-chip-desktop--selected" : ""}`}
                  />
                  <Chip
                    label={QUICK_NON_DIABETIC}
                    variant="outlined"
                    size="medium"
                    onClick={() => toggleQuickFilter(QUICK_NON_DIABETIC)}
                    className={`patient-records-filter-chip-desktop${quickFilters.includes(QUICK_NON_DIABETIC) ? " patient-records-filter-chip-desktop--selected" : ""}`}
                  />
                  <Chip
                    label={QUICK_FOLLOW_UP}
                    variant="outlined"
                    size="medium"
                    onClick={() => toggleQuickFilter(QUICK_FOLLOW_UP)}
                    className={`patient-records-filter-chip-desktop${quickFilters.includes(QUICK_FOLLOW_UP) ? " patient-records-filter-chip-desktop--selected" : ""}`}
                  />
                  {optometristRole === "Ophthalmologist" && (
                    <Chip
                      label={QUICK_ASSESSMENT_PENDING}
                      variant="outlined"
                      size="medium"
                      onClick={() => toggleQuickFilter(QUICK_ASSESSMENT_PENDING)}
                      className={`patient-records-filter-chip-desktop${quickFilters.includes(QUICK_ASSESSMENT_PENDING) ? " patient-records-filter-chip-desktop--selected" : ""}`}
                    />
                  )}
                  <Button
                    variant="outlined"
                    size="medium"
                    endIcon={<KeyboardArrowDownIcon />}
                    onClick={openDrFilterMenu}
                    className={`patient-records-filter-stage-btn${drMenuAnchor ? " patient-records-filter-stage-btn--open" : ""}`}
                  >
                    Diabetic Retinopathy Stage
                    {drGrades.length > 0 ? ` (${drGrades.length})` : ""}
                  </Button>
                  <Button
                    variant="outlined"
                    size="medium"
                    endIcon={<KeyboardArrowDownIcon />}
                    onClick={openDmeFilterMenu}
                    className={`patient-records-filter-stage-btn${dmeMenuAnchor ? " patient-records-filter-stage-btn--open" : ""}`}
                  >
                    DME
                    {dmeStages.length > 0 ? ` (${dmeStages.length})` : ""}
                  </Button>
                  <Button
                    variant="outlined"
                    size="medium"
                    endIcon={<KeyboardArrowDownIcon />}
                    onClick={openDateFilterMenu}
                    className={`patient-records-filter-stage-btn${dateMenuAnchor ? " patient-records-filter-stage-btn--open" : ""}`}
                  >
                    {screeningDateFilterLabel}
                  </Button>
                </Box>
              </Box>
            )}
            <Popover
              open={Boolean(drMenuAnchor)}
              anchorEl={drMenuAnchor}
              onClose={() => setDrMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              disableScrollLock
              PaperProps={{ className: "patient-records-stage-filter-popover" }}
            >
              <MenuList dense autoFocusItem={false} className="patient-records-stage-filter-menu-list">
                {DR_STAGE_OPTIONS.map((row, index) => (
                  <React.Fragment key={row.api}>
                    <Box className="patient-records-stage-filter-menu-row">
                      <FormControlLabel
                        className="patient-records-stage-filter-menu-form-label"
                        control={
                          <Checkbox
                            size="small"
                            checked={drGrades.includes(row.api)}
                            onChange={() => toggleDrGrade(row.api)}
                            className="patient-records-stage-filter-checkbox"
                          />
                        }
                        label={
                          <Typography className="patient-records-stage-filter-menu-option-label">
                            {row.label}
                          </Typography>
                        }
                      />
                    </Box>
                    {index < DR_STAGE_OPTIONS.length - 1 && (
                      <Divider className="patient-records-stage-filter-menu-divider" />
                    )}
                  </React.Fragment>
                ))}
              </MenuList>
            </Popover>
            <Popover
              open={Boolean(dmeMenuAnchor)}
              anchorEl={dmeMenuAnchor}
              onClose={() => setDmeMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              disableScrollLock
              PaperProps={{
                className:
                  "patient-records-stage-filter-popover patient-records-stage-filter-popover--dme",
              }}
            >
              <MenuList dense autoFocusItem={false} className="patient-records-stage-filter-menu-list">
                {DME_STAGE_OPTIONS.map((row, index) => (
                  <React.Fragment key={row.key}>
                    <Box className="patient-records-stage-filter-menu-row">
                      <FormControlLabel
                        className="patient-records-stage-filter-menu-form-label"
                        control={
                          <Checkbox
                            size="small"
                            checked={dmeStages.includes(row.key)}
                            onChange={() => toggleDmeStage(row.key)}
                            className="patient-records-stage-filter-checkbox"
                          />
                        }
                        label={
                          <Typography className="patient-records-stage-filter-menu-option-label">
                            {row.label}
                          </Typography>
                        }
                      />
                    </Box>
                    {index < DME_STAGE_OPTIONS.length - 1 && (
                      <Divider className="patient-records-stage-filter-menu-divider" />
                    )}
                  </React.Fragment>
                ))}
              </MenuList>
            </Popover>
            <Popover
              open={Boolean(dateMenuAnchor)}
              anchorEl={dateMenuAnchor}
              onClose={() => setDateMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              disableScrollLock
              PaperProps={{ className: "patient-records-stage-filter-popover" }}
            >
              <Box sx={{ padding: "16px", display: "flex", flexDirection: "column", gap: "14px", minWidth: "220px" }}>
                {renderDateFilterInput(
                  "From",
                  screeningDateFrom,
                  handleScreeningDateFromChange,
                  optometristRole === "Ophthalmologist" ? OPHTHALMOLOGIST_MIN_SCREENING_DATE : undefined,
                  screeningDateTo
                )}
                {renderDateFilterInput("To", screeningDateTo, handleScreeningDateToChange, screeningDateFrom, undefined)}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClearScreeningDateFilter}
                    sx={{ color: "#011F4B" }}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApplyScreeningDateFilter}
                    sx={{ backgroundColor: "#011F4B", "&:hover": { backgroundColor: "#011F4B" } }}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            </Popover>
            </Box>

            {!isMobile && hasActiveFilters && (
              <Typography
                component="button"
                type="button"
                onClick={clearAllFilters}
                className="patient-records-clear-filters--desktop"
              >
                Clear All
              </Typography>
            )}
          </Box>
        </div>
        </div>
        <div className="patientRecordsGrid">
          {allReports.length > 0 ? (
            allReports.map((report, index) => {
              // Same inference as detail page: feedback OR getInferenceFromData (so list referral matches /patientrecord/:id)
              const leftInference = report.feedback_data?.left_eye_correct_inference ||
                getInferenceFromData(report.ml_model?.classification?.left, report.ml_model?.model_outputs?.left);
              const rightInference = report.feedback_data?.right_eye_correct_inference ||
                getInferenceFromData(report.ml_model?.classification?.right, report.ml_model?.model_outputs?.right);
              const finalInference = { left: leftInference, right: rightInference };

              // Same feedback-corrected-first preference as DR above, applied to DME
              const leftDME = report.feedback_data?.left_eye_correct_dme ||
                report.ml_model?.model_outputs?.left?.drdme?.DME_grade || null;
              const rightDME = report.feedback_data?.right_eye_correct_dme ||
                report.ml_model?.model_outputs?.right?.drdme?.DME_grade || null;
              const finalDME = { left: leftDME, right: rightDME };

              return (
                <PatientRecordsReport
                  key={index}
                  report={report}
                  inference={finalInference}
                  dme={finalDME}
                  isMobile={isMobile}
                  dataSource="api"
                  optometristRole={optometristRole}
                  facilities={facilities}
                />
              );
            })
          ) : isOphthalmologist ? (
            (() => {
              const isPatientIdSearch = searchValue.trim().length >= 3;
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "60px 20px",
                    textAlign: "center",
                    gridColumn: "1 / -1",
                    minHeight: "300px",
                  }}
                >
                  <img
                    src={isPatientIdSearch ? "/assests/images/patientDoesNotExistIllustration.svg" : "/assests/images/patientNotFoundIllustration.svg"}
                    alt=""
                    style={{ width: "160px", height: "160px", marginBottom: "20px" }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "#011F4B", marginBottom: "8px" }}>
                    {isPatientIdSearch ? "Patient does not exist" : "Patient not found"}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#666", marginBottom: "20px" }}>
                    {isPatientIdSearch
                      ? "Please recheck Patient ID and search."
                      : "Please try narrowing the filters."}
                  </Typography>
                  {!isPatientIdSearch && (
                    <Button
                      variant="contained"
                      onClick={clearAllFilters}
                      sx={{ backgroundColor: "#0D1E83", "&:hover": { backgroundColor: "#051572" } }}
                    >
                      Reset Filters
                    </Button>
                  )}
                </Box>
              );
            })()
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 20px",
                textAlign: "center",
                gridColumn: "1 / -1", // Span all columns in the grid
                minHeight: "300px",
              }}
            >
              {/* Search Icon */}
              <Box
                sx={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "20px",
                }}
              >
                <SearchIcon sx={{ fontSize: "40px", color: "#ccc" }} />
              </Box>

              {/* No Results Message */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "600",
                  color: "#333",
                  marginBottom: "10px",
                }}
              >
                No Patient Found
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#666",
                  maxWidth: "400px",
                  marginBottom: "20px",
                }}
              >
                {searchValue.trim().length >= 3 
                  ? `No patients found matching "${searchValue.trim()}". Please try a different search term.`
                  : "No patient records available. Try searching by Patient ID or adjust your filters."
                }
              </Typography>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center" }}>
                {searchValue.trim().length >= 3 && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSearchValue("");
                      fetchData();
                    }}
                    sx={{
                      borderColor: "#0D1E83",
                      color: "#0D1E83",
                      "&:hover": {
                        borderColor: "#051572",
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    Clear Search
                  </Button>
                )}
                
                {optometristRole !== "Ophthalmologist" && (
                  <Button
                    variant="contained"
                    onClick={handleFloatingClick}
                    sx={{
                      backgroundColor: "#0D1E83",
                      "&:hover": {
                        backgroundColor: "#051572",
                      },
                    }}
                  >
                    Add New Patient
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </div>

        <Box className="patient-records-pagination">
          <Box className="patient-records-pagination-line" />
          {prev === "" || prev === null ? (
            <IconButton
              disabled
              size="small"
              aria-label="previous page"
              className="patient-records-pagination-icon--disabled"
            >
              <NavigateBeforeIcon className="patient-records-pagination-nav-icon" />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              onClick={handlePrevButtonClick}
              aria-label="previous page"
              className="patient-records-pagination-icon--prev"
            >
              <NavigateBeforeIcon className="patient-records-pagination-nav-icon" />
            </IconButton>
          )}
          <Typography component="span" className="patient-records-pagination-text">
            {count > 0
              ? `${startRecord}-${endRecord} out of ${count}`
              : `0-${endRecord} out of ${count}`}
          </Typography>
          {next === "" || next === null ? (
            <IconButton
              disabled
              size="small"
              aria-label="next page"
              className="patient-records-pagination-icon--disabled"
            >
              <NavigateNextIcon className="patient-records-pagination-nav-icon" />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              onClick={handleNextButtonClick}
              aria-label="next page"
              className="patient-records-pagination-icon--next"
            >
              <NavigateNextIcon className="patient-records-pagination-nav-icon" />
            </IconButton>
          )}
          <Box className="patient-records-pagination-line" />
        </Box>

        {optometristRole !== "Ophthalmologist" && <FloatingButton onClick={handleFloatingClick} />}
      </div>
      <LoadingComponent open={getData} text={false} />
    </>
  );
};

export default PatientRecords;
